import { type Booking, BookingStatus } from '../../../../../../../models/Scheduling'
import { formatSentDate, formatSuggestedTime } from '../../../../../../../views/Scheduling/bookingFormat'

export interface FutureMeetingRowModel {
  booking: Booking
  expired: boolean
  pastMeeting: boolean
}

const EXPIRABLE_STATUSES = [BookingStatus.OPPORTUNITY, BookingStatus.REQUESTED, BookingStatus.AWAITING_REPLY]

export const isExpired = (booking: Booking, now: Date): boolean => {
  if (!EXPIRABLE_STATUSES.includes(booking.status) || !booking.start) {
    return false
  }
  const start = new Date(booking.start)
  if (booking.end) {
    return start < now
  }
  return start < new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

export const isPastMeeting = (booking: Booking, now: Date): boolean => {
  if (booking.status !== BookingStatus.CONFIRMED) {
    return false
  }
  const reference = booking.end ?? booking.start
  return !!reference && new Date(reference) < now
}

export const getInviteStatusLabel = (booking: Booking): string => (booking.invitationSent ? 'Invite sent' : 'Booking link sent')

const sortRank = (row: FutureMeetingRowModel): number => {
  switch (row.booking.status) {
    case BookingStatus.OPPORTUNITY:
      return 0
    case BookingStatus.REQUESTED:
    case BookingStatus.AWAITING_REPLY:
      return 1
    case BookingStatus.CANCELLED:
      return 4
    default:
      return row.pastMeeting ? 3 : 2
  }
}

const startValue = (booking: Booking): number => (booking.start ? new Date(booking.start).getTime() : Number.POSITIVE_INFINITY)

export const getFutureMeetingRows = (bookings: Booking[], now: Date): FutureMeetingRowModel[] =>
  bookings
    .map((booking) => ({ booking, expired: isExpired(booking, now), pastMeeting: isPastMeeting(booking, now) }))
    .sort((a, b) => sortRank(a) - sortRank(b) || startValue(a.booking) - startValue(b.booking))

interface FutureMeetingTimeInput {
  start?: string
  end?: string
  suggestedDate?: string
  expired?: boolean
}

export const formatFutureMeetingTime = ({ start, end, suggestedDate, expired = false }: FutureMeetingTimeInput): string => {
  if (!start) {
    return formatSuggestedTime({ suggestedDate })
  }
  if (expired) {
    return formatSentDate(new Date(start))
  }
  if (end) {
    return formatSuggestedTime({ start, end })
  }
  return `${formatSentDate(new Date(start))} · Time to be set`
}
